// src/lib/auth.js
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { api, API } from "./api";

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// ---------- ROLE HELPERS ----------
const normalizeRole = (role) =>
  String(role || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/-+/g, "_");

const ADMIN_ROLES = new Set(["admin", "super_admin"]);
const isAdminRole = (role) => ADMIN_ROLES.has(normalizeRole(role));
// ---------------------------------

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiFilingEnabled, setAiFilingEnabled] = useState(false);
  const [flagLoading, setFlagLoading] = useState(true);

  useEffect(() => {
    Promise.all([checkAuth(), fetchFeatureFlag()]);
  }, []);

  useEffect(() => {
    const refreshFeatureFlag = () => {
      fetchFeatureFlag();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshFeatureFlag();
      }
    };

    window.addEventListener("focus", refreshFeatureFlag);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshFeatureFlag);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const checkAuth = async () => {
    const token =
      localStorage.getItem("access_token") || localStorage.getItem("token");

    if (token) {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
        localStorage.setItem("user", JSON.stringify(res.data));
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("access_token");
        localStorage.removeItem("id_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
      }
    }

    setLoading(false);
  };

  const fetchFeatureFlag = async () => {
    try {
      const res = await fetch(`${API}/config/public`, {
        cache: "no-store",
        headers: { "Cache-Control": "no-cache" },
      });
      const data = await res.json();
      setAiFilingEnabled(data?.features?.ai_filing_enabled === true);
    } catch {
      setAiFilingEnabled(false);
    } finally {
      setFlagLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (data) => {
    const res = await api.post("/auth/register", data);
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateUser,
        aiFilingEnabled,
        flagLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function getDefaultRouteForUser(user) {
  return isAdminRole(user?.user_type) ? "/admin" : "/dashboard";
}

export function normalizeUserRole(role) {
  return normalizeRole(role);
}